// Which design a unit shows or prints, and why: the server's order
// (store.LayoutFor, equipment spec §6 and §11.5) — the unit's effective choice,
// then the default (a kiosk: its operation profile's; a display, a counter display
// or a slip: the organisation's), then the design built into the server. A kiosk design of
// another operation profile is never used.
//
// A choice nobody has published is still answered, flagged: the server skips it
// until it is published, so the machine shows something else meanwhile.

import type { StoredLayout } from './api'
import type { LayoutKind } from '../layout/types'
import type { ProfileUnitKind, UnitSettings } from './types'

export type DesignSource = 'own' | 'profile' | 'default' | 'builtIn'

export interface ResolvedDesign {
  /** Absent when the built-in design is shown. */
  layout?: StoredLayout
  source: DesignSource
  /** Chosen, but never published. */
  unpublished?: true
}

/** The design kind a unit of this kind draws (or prints, with ticket); null for none. */
export function layoutKindFor(kind: ProfileUnitKind, ticket = false): LayoutKind | null {
  if (ticket) return kind === 'kiosk' ? 'ticket' : null
  if (kind === 'kiosk') return 'kiosk'
  if (kind === 'display') return 'display'
  if (kind === 'desk_display') return 'counter'
  return null
}

/**
 * The design a unit uses. eff is its effective settings; layoutSource says where
 * its choice came from (the instance's own override, the equipment profile, or
 * the kind's default), and is passed through when that choice is used.
 */
export function designFor(
  kind: ProfileUnitKind,
  eff: UnitSettings,
  layoutSource: 'own' | 'profile' | 'default',
  layouts: readonly StoredLayout[],
  operationProfileId?: string,
  ticket = false,
): ResolvedDesign {
  const want = layoutKindFor(kind, ticket)
  if (!want) return { source: 'builtIn' }
  const mine = layouts.filter(
    (l) => l.kind === want && (want !== 'kiosk' || l.operationProfileId === operationProfileId),
  )
  const chosenId = ticket ? eff.ticketLayoutId : eff.layoutId
  const chosen = chosenId ? mine.find((l) => l.id === chosenId) : undefined
  if (chosen) return found(chosen, layoutSource)
  const dflt = mine.find((l) => l.isDefault)
  return dflt ? found(dflt, 'default') : { source: 'builtIn' }
}

function found(layout: StoredLayout, source: DesignSource): ResolvedDesign {
  return layout.published ? { layout, source } : { layout, source, unpublished: true }
}
